import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, HeartHandshake, Users, LineChart, Mail, ArrowRight } from "lucide-react";

const IMG_HERO = "https://images.unsplash.com/photo-1653762378429-8030175fda56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const IMG_CONNECT = "https://images.unsplash.com/photo-1704793602305-78afd16cc043?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

function Step({ n, title, text }) {
  return (
    <div className="relative pl-14">
      <span className="absolute left-0 top-0 w-10 h-10 rounded-2xl bg-[#F2E9FA] text-[#3F3A44] grid place-items-center font-display font-bold">
        {n}
      </span>
      <h4 className="font-semibold text-[#2B2625] text-lg mb-1">{title}</h4>
      <p className="text-[#6E655F] leading-relaxed">{text}</p>
    </div>
  );
}

export default function Landing() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#6B21A8] bg-[#F4EAE1] px-3 py-1.5 rounded-full">
              <Sparkles size={13} /> Science-backed · 120 questions
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#2B2625] mt-5 leading-[1.05]">
              Understand yourself.<br />
              <span className="text-[#6B21A8]">Connect</span> with the people who matter.
            </h1>
            <p className="text-lg text-[#6E655F] leading-relaxed mt-5 max-w-xl">
              A warm, scientific look at who you really are — and how you and someone you care about
              truly click. Discover where you complement each other, and how to connect more deeply in
              life and at work.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link
                to="/start" data-testid="hero-begin-btn"
                className="inline-flex items-center gap-2 bg-[#6B21A8] hover:bg-[#4C1D95] text-white font-semibold py-3.5 px-7 rounded-xl transition-colors active:scale-[0.98] shadow-sm"
              >
                Discover your profile <ArrowRight size={18} />
              </Link>
              <span className="text-sm text-[#918780]">Includes 1 free invite for someone you choose</span>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative"
          >
            <img src={IMG_HERO} alt="Friends connecting warmly"
                 className="rounded-[2rem] w-full h-[440px] object-cover shadow-lg" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl border border-[#E8E1D9] shadow-md p-4 hidden sm:block">
              <div className="text-3xl font-display font-bold text-[#3F3A44]">92%</div>
              <div className="text-xs text-[#918780] font-medium">compatibility discovered</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value props */}
      <section className="bg-white border-y border-[#E8E1D9]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 grid sm:grid-cols-3 gap-8">
          {[
            { icon: LineChart, t: "A true picture of you", d: "Three respected scientific lenses combine into one remarkably accurate portrait of your personality." },
            { icon: HeartHandshake, t: "How you two connect", d: "See where you naturally complement each other, and the little things worth understanding." },
            { icon: Users, t: "Better bonds, everywhere", d: "Practical, warm guidance for your relationships at home, with family, and at work." },
          ].map((f) => (
            <div key={f.t}>
              <span className="w-12 h-12 rounded-2xl bg-[#F4EAE1] text-[#6B21A8] grid place-items-center mb-4">
                <f.icon size={22} />
              </span>
              <h3 className="font-semibold text-xl text-[#2B2625] mb-2">{f.t}</h3>
              <p className="text-[#6E655F] leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#2B2625] mb-8">How it works</h2>
          <div className="space-y-8">
            <Step n="1" title="Take the assessment" text="Answer 120 gentle, science-based questions. It takes about 12 minutes." />
            <Step n="2" title="Get your personal report" text="A beautiful two-page report with infographics — delivered here and to your inbox as a PDF." />
            <Step n="3" title="Invite someone you're curious about" text="Your first invite is included. They take it too, and get their own report." />
            <Step n="4" title="Discover how you connect" text="You both receive a shared report on how you complement each other and grow together." />
          </div>
        </div>
        <img src={IMG_CONNECT} alt="Human connection"
             className="rounded-[2rem] w-full h-[460px] object-cover shadow-md" />
      </section>

      {/* Pricing */}
      <section className="bg-[#3F3A44] text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">One simple, honest price</h2>
          <p className="text-[#E4D6F5] mb-10 max-w-xl mx-auto">
            Your full assessment, your personal report, and one free invite to someone you choose.
          </p>
          <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto text-left">
            <div className="bg-white/10 rounded-2xl p-7 border border-white/15">
              <div className="text-sm text-[#E4D6F5] font-semibold uppercase tracking-wider">India & regional</div>
              <div className="font-display text-4xl font-bold mt-2">Rs. 99</div>
            </div>
            <div className="bg-white/10 rounded-2xl p-7 border border-white/15">
              <div className="text-sm text-[#E4D6F5] font-semibold uppercase tracking-wider">Rest of the world</div>
              <div className="font-display text-4xl font-bold mt-2">US$ 9</div>
            </div>
          </div>
          <Link
            to="/start" data-testid="pricing-begin-btn"
            className="inline-flex items-center gap-2 bg-[#7C3AED] hover:bg-[#5B21B6] text-[#2B2625] font-semibold py-3.5 px-8 rounded-xl transition-colors active:scale-[0.98] mt-10"
          >
            Begin your journey <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-wrap items-center justify-between gap-4 text-sm text-[#918780]">
        <span className="font-display text-lg text-[#2B2625]">RelateMetrics</span>
        <span className="flex items-center gap-3">
          <a href="https://relatemetrics.app" className="hover:text-[#6B21A8] transition-colors" data-testid="footer-domain">relatemetrics.app</a>
          <span className="flex items-center gap-2"><Mail size={14} /> Understand yourself. Connect with others.</span>
        </span>
      </footer>
    </div>
  );
}
